import React, { useRef, useState, useCallback } from 'react'
import {
  Button,
  Dialog,
  Input,
  Switch,
  Select,
  Toast,
  Tooltip,
  Icon,
  Checkbox,
} from 'common/ui'
import { useDioxContext } from 'common/hooks'
import { token as getTokenService, dapp as getDAppService } from 'common/api'
import styled from 'styled-components'
import { useEffect } from 'react'
import { makeFinishUrl } from '../utils'
import {
  DIO_AMOUNT,
  DIO_AMOUNT_ERROR,
  MINTER_ERROR,
  MINTER_PLACE,
  MINTER_REG,
  NUMBER_ERROR,
  NUMBER_MAX_ERROR,
  getTipText,
} from '../constants'
import { withRouter, NextRouter, useRouter } from 'next/router'
import {
  NETS,
  bignumberMult,
  bignumberPlus,
  getDefaultToken,
} from 'common/utils'

const tokenService = getTokenService()
const dappService = getDAppService()
const CreateTokenStyled = styled.div`
  margin-top: 40px;
  width: 340px;
  .label {
    margin-bottom: 12px;
    font-weight: 500;
    font-size: 14px;
    line-height: 18px;
    color: #a3a3a3;
    &:not(:nth-child(1)) {
      margin-top: 24px;
    }
  }
  .supply {
    font-weight: 400;
    font-size: 15px;
    line-height: 18px;
    display: flex;
    align-items: center;
    color: #ffffff;
    margin-top: 32px;
    justify-content: space-between;
  }
  .decimals {
    width: 160px;
  }
  .btn-group {
    display: flex;
    justify-content: end;
    align-items: center;
    margin-top: 58px;
    .cancel-btn {
      margin-right: 16px;
    }
  }
  .amount-tip {
    display: flex;
    align-item: center;
    svg {
      margin-left: 4px;
      width: 16px;
      height: 16px;
      cursor: pointer;
    }
    .tooltip-container {
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
  .amount-relative {
    position: relative;
    .amount-unit {
      position: absolute;
      right: 12px;
      top: 15px;
      font-weight: 400;
      font-size: 15px;
      line-height: 18px;
      color: #a3a3a3;
    }
  }
  .minter-flag {
    margin-top: 12px;
    color: #ffffff;
    font-size: 24px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px;
    display: inline-flex;
    align-items: center;
    span {
      font-size: 12px;
      margin-left: 8px;
    }
    &.disabled {
      color: #6b6b6b;
    }
  }
`
type CreateTokenContentProps = {
  onVerify: (result: boolean, txn?: string) => void
}
const SYMBOL_PLACE = '3-8 capital letters - #'
const SYMBOL_REPREAT = 'Symbol is taken'
const SymboleReg = new RegExp(/^[A-Z-#]{3,8}$/)
const SupplyReg = new RegExp(/^[0-9]{1,}$/)
const DEPOSIT_REPREAT = 'Positive integer'
const DecimalsOpt: Array<{ label: string | number; value: number }> = []
for (let i = 4; i < 19; i++) {
  DecimalsOpt.push({ label: i, value: i })
}
const CreateTokenContent = ({ onVerify }: CreateTokenContentProps) => {
  const [symbol, setSymbol] = useState('')
  const [symbolError, setSymbolError] = useState('')
  const [canMint, setCanMint] = useState<boolean>()
  const [minter, setMinter] = useState<string>()
  const [minterError, setMinterError] = useState('')
  const [minterFlag, setMinterFlag] = useState(false)
  const [initialSupply, setInitialSupply] = useState('')
  const [initialSupplyError, setInitialSupplyError] = useState('')
  const [decimals, setDecimals] = useState(8)
  const [amount, setAmount] = useState<string>()
  const [amountError, setAmountError] = useState<string>()
  const [disabled, setDisabled] = useState(true)
  const [loading, setLoading] = useState(false)
  const { dioxide } = useDioxContext()
  const defaultToken = getDefaultToken()

  const verifySymbol = useCallback(async () => {
    if (symbol === undefined || !SymboleReg.test(symbol) || symbol.length > 8) {
      setSymbolError(SYMBOL_PLACE)
      return false
    }
    const res = await tokenService.getTokenInfo(symbol)
    if (res.Result?.Symbol) {
      setSymbolError(SYMBOL_REPREAT)
      return false
    }
    setSymbolError('')
    return true
  }, [symbol])

  const verifySupply = useCallback(async () => {
    if (initialSupply?.length > 12) {
      setInitialSupplyError(NUMBER_MAX_ERROR)
      return false
    }
    if (!initialSupply || !SupplyReg.test(initialSupply)) {
      setInitialSupplyError(NUMBER_ERROR)
      return false
    }
    if (!canMint && initialSupply == '0') {
      setInitialSupplyError(NUMBER_ERROR)
      return false
    }
    setInitialSupplyError('')
    return true
  }, [initialSupply, canMint])

  const verifyAmount = useCallback(() => {
    const min = defaultToken.SYMBOL === NETS.DIOXIDE ? 1 : 0.000001
    if (
      amount === undefined ||
      Number(amount) < min ||
      !DIO_AMOUNT.test(amount)
    ) {
      setAmountError(DIO_AMOUNT_ERROR())
      return false
    } else {
      setAmountError('')
      return true
    }
  }, [amount, onVerify, defaultToken])

  const verifyMinter = useCallback(async () => {
    if (minter) {
      if (!MINTER_REG.test(minter)) {
        setMinterError(MINTER_ERROR)
        return false
      }
      const res = await dappService.checkContract(Number(minter).toString(10))
      if (res?.Result?.Contract) {
        setMinterError('')
        return true
      } else {
        setMinterError(MINTER_ERROR)
        return false
      }
    } else {
      setMinterError('')
      return true
    }
  }, [minter, onVerify])

  const handleClose = useCallback(() => {
    onVerify && onVerify(true)
  }, [onVerify])

  useEffect(() => {
    if (amount && !amountError && !symbolError && symbol) {
      setDisabled(false)
    } else {
      setDisabled(true)
    }
  }, [symbolError, symbol, amount, amountError])

  useEffect(() => {
    canMint !== undefined && verifySupply()
    if (!canMint) {
      setMinter(undefined)
      setMinterError('')
      setMinterFlag(false)
    }
  }, [canMint, verifySupply])

  const handleConfirm = async () => {
    const smybolResult = await verifySymbol()
    const amountResult = await verifyAmount()
    const supplyResult = await verifySupply()
    const minterResult = await verifyMinter()
    if (!smybolResult || !amountResult || !supplyResult || !minterResult)
      return false
    setLoading(true)
    try {
      const MinterFlags = canMint ? (minterFlag && minter ? 3 : 1) : 0
      const txn = await dioxide.request({
        method: 'send_transaction',
        params: {
          func: 'core.delegation.create_token',
          args: {
            Symbol: symbol,
            Minter:
              canMint && minter
                ? bignumberPlus(Number(minter).toString(10), 0).toNumber()
                : 0,
            TokenStates: canMint ? 0 : 1,
            InitSupply: bignumberMult(initialSupply, 10 ** decimals).toString(),
            Deposit: bignumberMult(amount || 0, 10 ** defaultToken?.DECIMAL),
            Decimals: decimals,
            MinterFlags,
          },
        },
      })
      onVerify && onVerify(true, txn)
    } catch (e: any) {
      Toast({ text: e?.message as string })
    }
    setLoading(false)
  }

  const handleChangeMinter = (v: string) => {
    setMinter(v)
  }

  return (
    <CreateTokenStyled>
      <div className="label">Symbol</div>
      <Input
        value={symbol}
        onChange={setSymbol}
        onBlur={verifySymbol}
        placeholder={SYMBOL_PLACE}
        error={symbolError}
      />
      <div className="label">Decimals</div>
      <Select
        className="decimals"
        selected={decimals}
        options={DecimalsOpt}
        onChange={(e) => setDecimals(e as number)}
      />
      <div className="label">Initial Supply</div>
      <Input
        type="number"
        value={initialSupply}
        onChange={setInitialSupply}
        onBlur={verifySupply}
        placeholder={NUMBER_ERROR}
        error={initialSupplyError}
      />
      <div className="label amount-tip">
        Top-up Amount
        <Tooltip
          placement="topLeft"
          overlayStyle={{ width: '386px', height: '188px' }}
          overlay={getTipText('Token')}
        >
          <Icon type="tip" />
        </Tooltip>
      </div>
      <div className="amount-relative">
        <Input
          className="amount-input"
          type="number"
          maxLength={18}
          maxValue={184467440737}
          value={amount}
          onChange={setAmount}
          onBlur={verifyAmount}
          error={amountError}
          placeholder={DEPOSIT_REPREAT}
        />
        <span className="amount-unit">{defaultToken.SYMBOL}</span>
      </div>
      <div className="supply">
        <span>Allowed Add-on supply</span>
        <Switch value={!!canMint} onChange={setCanMint} />
      </div>
      {canMint ? (
        <>
          <div className="label">Set Minter ( Optional )</div>
          <Input
            value={minter}
            onChange={handleChangeMinter}
            onBlur={verifyMinter}
            placeholder={MINTER_PLACE}
            error={minterError}
          />
          <Checkbox
            defaultChecked={minterFlag}
            className="minter-flag"
            disabled={!minter}
            onChange={setMinterFlag}
          >
            <span>Minter Irrevocable</span>
          </Checkbox>
        </>
      ) : null}

      <div className="btn-group">
        <Button
          loading={loading}
          className="cancel-btn"
          type="outline"
          onClick={handleClose}
        >
          Cancel
        </Button>
        <Button
          loading={loading}
          disabled={disabled}
          type="primary"
          onClick={handleConfirm}
        >
          Submit
        </Button>
      </div>
    </CreateTokenStyled>
  )
}
type CreateTokenBtnProps = {
  children: JSX.Element | string
  router: NextRouter
  icon?: string
}
const CreateTokenBtn = ({ children, router, icon }: CreateTokenBtnProps) => {
  const creatTokenRef = useRef<{ close: () => void }>()
  const { asPath, pathname, push } = useRouter()

  useEffect(() => {
    if (asPath && asPath.match('action=')) {
      const action = asPath.match(/action=(\S+)/)
      if (action && action[1]) {
        const Event = action[1]
        if (Event === 'create') {
          handleCreatToken()
        }
        setTimeout(() => {
          push(pathname)
        }, 1000)
      }
    }
  }, [asPath])

  const handleVerify = useCallback(
    (result, txn) => {
      if (result) {
        creatTokenRef.current?.close && creatTokenRef.current?.close()
        txn && router.push(makeFinishUrl(txn, { target: 'token' }))
      }
    },
    [creatTokenRef]
  )

  const handleCreatToken = () => {
    Dialog({
      title: 'Create Token',
      content: <CreateTokenContent onVerify={handleVerify} />,
      ref: creatTokenRef,
    })
  }

  useEffect(() => {
    return () => {
      creatTokenRef.current?.close && creatTokenRef.current?.close()
    }
  }, [creatTokenRef])

  return (
    <Button icon={icon} onClick={handleCreatToken} type="primary" size="large">
      {children}
    </Button>
  )
}

export default withRouter(CreateTokenBtn)
